import { PensionPlanTransfer } from '../../api/pension-plan-transfers/interfaces/PensionPlanTransfer';
import {
	PENDING_PERSONAL_OPTION_KEY,
	ViewOptionPlan,
	isLivePlan,
	isPersonalViewPlan
} from '../viewOptions';
import {
	AccionPendiente,
	ESTADOS_VIVOS,
	accionPendienteDelTraspaso,
	estadoDeIdentidad
} from '../traspasos/tarjetasDeTraspaso';

export interface EntradaOperacionPendiente {
	plans: ViewOptionPlan[];
	pendingPersonalPlan: boolean;
	kycStatus: string | null | undefined;
	/** `/kyc/status` ya ha respondido. «Aún no lo sé» no es «no hace falta». */
	kycResuelto: boolean;
	transfers: PensionPlanTransfer[];
	signedBulletinIds: string[];
}

export interface OperacionPendiente {
	accion: AccionPendiente;
	/**
	 * Adónde lleva la tarjeta: el plan donde Inicio tiene ese flujo. El traspaso de empleo, a su
	 * plan; la identidad y los traspasos personales, al plan personal (o al que está en proceso,
	 * que abre la vista personal).
	 */
	planId: string;
	/** El traspaso del destino o la firma: el Resumen elige destino o firma sobre él. */
	transfer?: PensionPlanTransfer;
}

/** Cuándo se pidió lo que ahora falta: el destino, al pedir el traspaso; la firma, al bloquear el plan. */
const fechaDe = (accion: AccionPendiente, transfer: PensionPlanTransfer): string =>
	accion.tipo === 'firma' ? transfer.planLockedAt ?? transfer.createdAt : transfer.createdAt;

/**
 * La operación que va arriba del Resumen (product#6, §3.3): UNA, y la identidad primero.
 *
 * - La identidad es de la persona y solo se pide si tiene un plan personal (o uno en proceso): el
 *   plan de empleo no pide KYC. Mismos estados que en Inicio (`estadoDeIdentidad`).
 * - Si no, el destino o la firma de algún traspaso vivo, con las mismas reglas que Inicio
 *   (`accionPendienteDelTraspaso`, con las cinco puertas de `puedeFirmarBoletin`). Si hay varias,
 *   gana la más reciente.
 */
export function selectPendingOperation({
	plans,
	pendingPersonalPlan,
	kycStatus,
	kycResuelto,
	transfers,
	signedBulletinIds
}: EntradaOperacionPendiente): OperacionPendiente | null {
	if (!kycResuelto) return null;
	const personal = plans.find((plan) => isLivePlan(plan) && isPersonalViewPlan(plan));
	const tienePlanPersonal = pendingPersonalPlan || !!personal;
	const planPersonal = personal?.id ?? PENDING_PERSONAL_OPTION_KEY;
	const estado = estadoDeIdentidad(kycStatus);
	if (tienePlanPersonal && estado) {
		return { accion: { tipo: 'identidad', estado }, planId: planPersonal };
	}

	// 🔴 Un traspaso enlazado a un plan de EMPLEO no pide destino ni firma: comparsa lo enlaza al
	// aceptar la STDE y, con 2+ planes, no le rellena destino. En empleo no se elige destino.
	const deEmpleo = new Set(
		plans.filter((plan) => !isPersonalViewPlan(plan)).map((plan) => plan.id)
	);
	const candidatas = transfers
		.filter((t) => ESTADOS_VIVOS.includes(t.status ?? ''))
		.filter((t) => !t.userEppConfigId || !deEmpleo.has(t.userEppConfigId))
		.map((transfer) => ({
			transfer,
			accion: accionPendienteDelTraspaso({
				traspasoVivo: transfer,
				kycStatus,
				kycResuelto,
				signedBulletinIds
			})
		}))
		.filter(
			(c): c is { transfer: PensionPlanTransfer; accion: AccionPendiente } =>
				c.accion?.tipo === 'destino' || c.accion?.tipo === 'firma'
		)
		.sort((a, b) => fechaDe(b.accion, b.transfer).localeCompare(fechaDe(a.accion, a.transfer)));

	const elegida = candidatas[0];
	return elegida
		? {
				accion: elegida.accion,
				planId: elegida.transfer.userEppConfigId ?? planPersonal,
				transfer: elegida.transfer
			}
		: null;
}
